import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, contentChild, inject, input, output, signal, TemplateRef } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import type { Sort } from '@angular/material/sort';
import { MatTooltip } from '@angular/material/tooltip';
import { Router, RouterLink } from '@angular/router';
import { RestrictedElementDirective } from '@auth/directives';
import { EntityStatus, fileUtils, SessionStorageService, signalHelpers } from '@core';
import { IncidentSeverityCountComponent } from '@incidents';
import { UserRolesIds } from '@models';
import { Store } from '@ngrx/store';
import { AuthSelectors } from '@ngrx-store';
import { AvsDatagridComponent, ConfirmationDialogComponent, DatagridCellDirective, type DatagridColumn, NoDataPlaceholderComponent, SkeletonTableComponent } from '@ui';
import { filter } from 'rxjs';

import { DeviceDisplayStatusComponent, FirmwareStatusComponent } from '../../components';
import { DeviceTableCols, type NormalizedDeviceData } from '../../models';
import { DevicesActions } from '../../ngrx-store';
import { DeviceAttributePipe } from '../../pipes';
import { DeviceServiceV2 } from '../../services';
import { DevicesStore } from '../../stores';

interface Filters {
  parent?: string;
  query?: string;
  space?: string;
  status?: string[];
}

const ROW_HEIGHT = 44;

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'flex flex-col',
  },
  imports: [
    CommonModule,
    RestrictedElementDirective,
    AvsDatagridComponent,
    DatagridCellDirective,
    NoDataPlaceholderComponent,
    RouterLink,
    SkeletonTableComponent,
    DeviceAttributePipe,
    DeviceDisplayStatusComponent,
    IncidentSeverityCountComponent,
    FirmwareStatusComponent,
    MatIcon,
    MatTooltip,
  ],
  providers: [DevicesStore],
  selector: 'avs-devices-table',
  styleUrl: './devices-table.component.scss',
  templateUrl: './devices-table.component.html',
})
export class DevicesTableV2Component {
  displayedColumns = input.required<string[]>();
  filters = input<Filters>();
  inputConfig = input<{
    filtersSkeleton?: boolean;
    largePlaceholder?: boolean;
    placeholder?: string;
    reportMode?: boolean;
    selectAll?: boolean;
    skipFirstLoad?: boolean;
    sorting?: boolean;
    stickyColumns?: boolean;
    zoomControls?: boolean;
  }>({}, { alias: 'config' });
  isDataLoading = signal(false);
  isReady = signal<boolean>(false);
  selected = input<string[]>();
  selectionChanged = output<string[]>();
  tableId = input.required<string>();
  data = computed(() => this.devicesStore.entities());
  protected config = computed(() => {
    const config = this.inputConfig();

    return {
      filtersSkeleton: true,
      largePlaceholder: true,
      sorting: true,
      stickyColumns: true,
      zoomControls: true,
      ...config,
    };
  });
  protected readonly INCIDENT_SEVERITY_BADGE_CLASS = {
    critical: 'bg-ui-red-50 text-ui-red-300',
    warning: 'bg-ui-yellow-100 text-ui-yellow-450',
  };
  protected customActions = contentChild<TemplateRef<any>>('actions');
  protected devicesStore = inject(DevicesStore);
  protected eEntityStatus = EntityStatus;
  protected eUserRolesIds = UserRolesIds;

  protected filtersApplied = computed(() => {
    const { space, status, query, ...filters } = this.filters() ?? {};

    return Object.keys(filters).length > 0 || (query?.length ?? 0) > 0;
  });

  protected isSelectionEnabled = computed(() =>
    this.displayedColumns().includes(DeviceTableCols.SELECT),
  );

  protected hasMore = computed(() => {
    const count = this.devicesStore.count();

    return count === null || this.devicesStore.offset() < count;
  });

  protected userRole = toSignal(inject(Store).select(AuthSelectors.userRole));

  protected readonly columns: DatagridColumn<NormalizedDeviceData>[] = [
    {
      hidden: () => this.isColumnHidden(DeviceTableCols.INDEX),
      id: DeviceTableCols.INDEX,
      resizable: false,
      sortable: false,
      sticky: 'start',
      title: '№',
    },
    {
      hidden: () => this.isColumnHidden(DeviceTableCols.DEVICE_NAME),
      id: DeviceTableCols.DEVICE_NAME,
      sortable: true,
      sticky: 'start',
      title: 'Device Name',
    },
    {
      hidden: () => this.isColumnHidden(DeviceTableCols.BUILDING),
      id: DeviceTableCols.BUILDING,
      sortable: true,
      title: 'Building',
    },
    {
      hidden: () => this.isColumnHidden(DeviceTableCols.FLOOR),
      id: DeviceTableCols.FLOOR,
      sortable: true,
      title: 'Floor',
    },
    {
      hidden: () => this.isColumnHidden(DeviceTableCols.ROOM),
      id: DeviceTableCols.ROOM,
      sortable: true,
      title: 'Room',
    },
    {
      hidden: () => this.isColumnHidden(DeviceTableCols.STATUS),
      id: DeviceTableCols.STATUS,
      sortable: true,
      title: 'Monitoring Status',
    },
    {
      hidden: () => this.isColumnHidden(DeviceTableCols.DEVICE_TYPE),
      id: DeviceTableCols.DEVICE_TYPE,
      sortable: true,
      title: 'Device Type',
    },
    {
      hidden: () => this.isColumnHidden(DeviceTableCols.MANUFACTURER),
      id: DeviceTableCols.MANUFACTURER,
      sortable: true,
      title: 'Manufacturer',
    },
    {
      hidden: () => this.isColumnHidden(DeviceTableCols.MODEL),
      id: DeviceTableCols.MODEL,
      sortable: true,
      title: 'Model',
    },
    {
      hidden: () => this.isColumnHidden(DeviceTableCols.SERIAL_NUMBER),
      id: DeviceTableCols.SERIAL_NUMBER,
      sortable: true,
      title: 'Serial №',
    },
    {
      hidden: () => this.isColumnHidden(DeviceTableCols.IP_ADDRESS),
      id: DeviceTableCols.IP_ADDRESS,
      sortable: false,
      title: 'IP address',
    },
    {
      hidden: () => this.isColumnHidden(DeviceTableCols.LAST_REPORTED),
      id: DeviceTableCols.LAST_REPORTED,
      sortable: false,
      title: 'Last Reported',
    },
    {
      hidden: () => this.isColumnHidden(DeviceTableCols.MAC_ADDRESS),
      id: DeviceTableCols.MAC_ADDRESS,
      sortable: true,
      title: 'Mac Address',
    },
    {
      hidden: () => this.isColumnHidden(DeviceTableCols.FIRMWARE),
      id: DeviceTableCols.FIRMWARE,
      sortable: true,
      title: 'Firmware',
    },
    {
      cellClass: 'text-center',
      hidden: () => this.isColumnHidden(DeviceTableCols.INCIDENTS) || this.isArchivedFilter(),
      id: DeviceTableCols.INCIDENTS,
      sortable: true,
      title: 'Incidents',
    },
    {
      hidden: () => this.isColumnHidden(DeviceTableCols.IS_DC),
      id: DeviceTableCols.IS_DC,
      sortable: true,
      title: 'Is DC',
    },
    {
      hidden: () => this.isColumnHidden(DeviceTableCols.ACTIONS)
        || (this.isArchivedFilter() && this.userRole() === UserRolesIds.ClientAdmin),
      id: DeviceTableCols.ACTIONS,
      sortable: false,
      sticky: 'end',
      title: '',
    },
  ];

  protected TYPICAL_COL_HEIGHT = ROW_HEIGHT;

  private deviceServiceV2 = inject(DeviceServiceV2);
  private dialog = inject(MatDialog);
  private router = inject(Router);
  private sessionStorageService = inject(SessionStorageService);
  private store = inject(Store);

  constructor() {
    signalHelpers.untrackedEffect([this.devicesStore.sort, this.filters], async (_sort, _filters) => {
      return this.refresh();
    });
  }

  export(format: 'json' | 'csv') {
    const filters = this.filters();
    const sort = this.devicesStore.sort();

    this.deviceServiceV2
      .list({
        filters,
        query: filters?.query,
        sort: sort?.direction ? sort : undefined,
      })
      .subscribe(({ data }) => {
        const fileName = 'devices report';
        const columns = this.displayedColumns()
          .filter(column => column !== 'actions' && column !== 'status')
          .map(column => (column === 'index' ? 'id' : column));
        const reportData = fileUtils.prepareDataForDownloading(columns, data);

        if (format === 'csv') {
          fileUtils.exportDataToCSV(reportData, fileName);
        } else {
          fileUtils.exportDataToJSON(reportData, fileName);
        }
      });
  }

  async refresh() {
    this.devicesStore.clear();
    await this.loadDevices(this.devicesStore.sort(), this.filters());
  }

  replaceData(data: NormalizedDeviceData[]) {
    this.devicesStore.replaceData(data);
  }

  protected archiveDevice(device: NormalizedDeviceData) {
    this.openConfirmationDialog({
      data: { status: EntityStatus.Archived },
      description: `Are you sure you want to add ${device.deviceName} to archive?`,
      deviceId: device.id,
      locationId: device.buildingId,
      title: 'Add to Archive',
    });
  }

  protected deleteDevicePermanently(device: NormalizedDeviceData) {
    ConfirmationDialogComponent.open(this.dialog, {
      cancelBtnText: 'Cancel',
      confirmBtnText: 'Delete',
      description: `Are you sure you want to delete ${device.deviceName} ?`,
      showWarning: true,
      title: 'Delete',
    })
      .pipe(filter(Boolean))
      .subscribe(async () => {
        await this.devicesStore.deleteDevice(device);
        // TODO (serhiisol): legacy store action
        this.store.dispatch(DevicesActions.dropDevice({ deviceId: device.id }));
      });
  }

  protected async goToDevice(device: NormalizedDeviceData) {
    const url = ['/devices', device.buildingId, device.id];

    if (this.tableId) {
      this.sessionStorageService.saveLastSelectedItems(this.tableId(), { title: device.deviceName, url });
    }

    await this.router.navigate(url);
  }

  protected pauseDevice(device: NormalizedDeviceData) {
    this.openConfirmationDialog({
      data: { status: EntityStatus.Paused },
      description: `Are you sure you want to pause ${device.deviceName} ?`,
      deviceId: device.id,
      locationId: device.buildingId,
      title: 'Pause',
    });
  }

  protected restoreDevice(device: NormalizedDeviceData) {
    this.openConfirmationDialog({
      data: { status: EntityStatus.Active },
      description: `Are you sure you want to restore ${device.deviceName} from archive?`,
      deviceId: device.id,
      locationId: device.buildingId,
      title: 'Restore',
    });
  }

  protected resumeDevice(device: NormalizedDeviceData) {
    this.openConfirmationDialog({
      data: { status: EntityStatus.Active },
      description: `Are you sure you want to resume ${device.deviceName} ?`,
      deviceId: device.id,
      locationId: device.buildingId,
      title: 'Resume',
    });
  }

  protected async loadMore() {
    if (this.isDataLoading()) {
      return;
    }

    await this.loadDevices(this.devicesStore.sort(), this.filters());
  }

  private isColumnHidden(id: string) {
    return !this.displayedColumns().includes(id);
  }

  private isArchivedFilter(): boolean {
    return this.filters()?.status?.[0] === 'archived';
  }

  private async loadDevices(sort?: Sort | null, filters?: Filters) {
    if (this.isDataLoading()) {
      return;
    }

    this.isDataLoading.set(true);

    await this.devicesStore.loadDevices({
      filters,
      query: filters?.query,
      sort: sort?.direction ? sort : undefined,
    });

    this.isDataLoading.set(false);
    this.isReady.set(true);
  }

  private openConfirmationDialog(
    {
      title,
      description,
      locationId,
      deviceId,
      data,
    }: {
      data: { [_: string]: string; };
      description: string;
      deviceId: string;
      locationId: string;
      title: string;
    }) {
    ConfirmationDialogComponent.open(this.dialog, {
      description,
      title,
    })
      .subscribe(confirmation => {
        if (confirmation && locationId && deviceId && data) {
          const shouldBeRemoved = !(this.filters()?.status || []).includes(data['status']);

          this.devicesStore.updateDeviceEntity(deviceId, { status: data['status'] as EntityStatus });
          if (shouldBeRemoved) {
            this.devicesStore.dropDevice(deviceId);
          }

          // TODO (serhiisol): legacy action
          this.store.dispatch(DevicesActions.updateDeviceData({ data, deviceId, locationId }));
        }
      });
  }
}
